import { addMonths, endOfMonth } from "date-fns";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR, { cache, ConfigInterface, mutate } from "swr";

import {
  isRecurrenceInterval,
  RecurrenceInput,
  RecurrenceInterval,
} from "./recurrence";

import { toDay } from "../lib/datetime";

import { useAuth0 } from "../core";
import {
  Booking,
  BookingType as BookingUnit,
  LightSpace,
  PartnerLocation,
  Pricing,
  PricingType,
  ProductType,
  QuoteItem,
  SourceType,
  Space,
  SpaceLayout,
  toOrderable,
  toPromotion,
  toQuoteItemPrice,
} from "../data";
import {
  APIError,
  requestJson,
  toAmenity,
  toErrorBody,
  toLightSpace,
  toLocation,
  toSpace as legacyToSpace,
  toSpace,
  toSpaceBookingPolicy,
  PartnerOfferingParams,
} from "../data/api";
import {
  CatalogMapOptions,
  PartnerCatalog,
  PartnerCatalogCategory,
  toPartnerCatalog,
  toPartnerCatalogCategory,
} from "../data/catalog";
import {
  toPartnerBookingHour,
  toPartnerBookingPolicy,
  toPartnerTestimonial,
} from "../data/partners";
import {
  Offering,
  OfferingMenu,
  PartnerOffering,
  toImage,
  toPartnerOfferingFilterConfig,
  toPartnerOfferingImage,
} from "../data/partner_offering";
import { ImageVersion } from "../data/image";
import {
  IntegrationType,
  ResourceIntegration,
  toResourceIntegration,
} from "../data/resource_integration";
import { useI18n } from "../lib/i18n/i18n_provider";
import { isNotEmpty } from "../lib/js_utils/array_utils";
import { useAuth } from "./auth_portal_provider";
import { toFeeLine, toQuote } from "./hooks/use_quote_query";
import { addHours, getDurationInHours } from "./time_utils";
import { UTMParams } from "./utm";
import { isUndefined } from "lodash";
import { LiteralObject } from "../types/literal_object";
import { toPartnerOffering } from "../fm/data/partner_offering";
import {
  RecurringBooking,
  toRecurringBooking,
} from "../fm/data/recurring_booking";
import { DynamicPricingAdjustmentType } from "@/offering_v2/hooks/use_dynamic_rates_query";
import { hasSmartPricingSearchParam } from "@/offering_v2/utils/has_smart_pricing_search_param";
import { isEmail } from "@/lib/validation/string";
import { AddOnOrderParams } from "./order_params";
import { toNumber } from "@/lib/js_utils/number_utils";

export enum OrderableType {
  Booking = "Booking",
  AddOn = "AddOn",
  Membership = "Membership",
}

export enum QuoteItemOrderableType {
  Space = "Space",
  SpaceOffering = "SpaceOffering",
  Membership = "Membership",
}

export enum DiscountType {
  FixedPrice = "fixed_price",
  Amount = "amount",
  Percentage = "percentage",
  OriginalPrice = "original_price",
}

export enum BookingType {
  Hourly = "Hourly",
  Daily = "Daily",
}

export enum OfferingMessageType {
  PartnerOffering = "PartnerOffering",
  PartnerCatalog = "PartnerCatalog",
}

/**
 * @deprecated use `useSpaceV2Query` with suspense instead
 */
export function useSpaceQuery(spaceID?: string | null) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    spaceID ? `/api/v2/spaces/${spaceID}${qs}` : null
  );

  return {
    data: data ? legacyToSpace(data) : undefined,
    loading: spaceID && !data && !error,
    error,
  };
}

export function useSpaceV2Query(spaceID: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;
  if (hasSmartPricingSearchParam()) {
    qs += "&smart_pricing=true";
  }

  const { data } = useSWR(`/api/v2/spaces/${spaceID}${qs}`, { suspense: true });

  return toSpace(data);
}

export function useSpaceMaybeQuery(spaceID?: string): Space | undefined {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(spaceID ? `/api/v2/spaces/${spaceID}${qs}` : null, {
    suspense: true,
  });

  return data ? toSpace(data) : undefined;
}

export function useOfferingsMenuQuery(partnerID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;
  const response = useSWR(
    partnerID ? `/api/v2/partners/${partnerID}/offerings/${qs}` : null
  );

  return response.data ? toOfferingsMenu(response.data) : null;
}

function toOfferingsMenu(data: any): OfferingMenu {
  return {
    showOfferingMenu: data.offering_menu,
    offerings: data.offerings.map(toOfferings),
  };
}

export function useOfferingsQuery(partnerID: string): PartnerOffering[] {
  const response = useSWR(
    `/api/v3/partners/${partnerID}/offerings?exclude_marketing=true`,
    { suspense: true }
  );

  return response.data.data.map(toPartnerOffering);
}

function toOfferings(data: any): Offering {
  return {
    id: data.id,
    slug: data.slug,
    menuDisplayName: data.menu_display_name,
  };
}

interface SpacePhysicalSpacesParams {
  startDate?: string;
  endDate?: string;
}

export function useSpacePhysicalSpacesQuery(
  spaceID: string,
  params: SpacePhysicalSpacesParams
): PhysicalSpace[] {
  let searchParams = new URLSearchParams();
  let qs = "";

  if (params) {
    if (params.startDate) {
      searchParams.set("start_date", params.startDate);
    }
    if (params.endDate) {
      searchParams.set("end_date", params.endDate);
    }

    qs = `?${searchParams.toString()}`;
  }

  const { data } = useSWR(`/api/v2/spaces/${spaceID}/physical_spaces${qs}`, {
    suspense: true,
  });

  return data.map(toPhysicalSpace);
}

export function usePhysicalSpaceQuery(physicalSpaceID?: string): PhysicalSpace {
  const { data } = useSWR(`/api/v2/physical_spaces/${physicalSpaceID}`, {
    suspense: true,
  });

  return toPhysicalSpace(data.physical_space);
}

interface SpacesParams {
  locale: string;
}

const toSpacesSearchParams = (params: SpacesParams) => {
  const searchParams = new URLSearchParams();

  searchParams.set("locale", params.locale);

  return `?${searchParams.toString()}`;
};

function sliceIntoChunks<T>(arr: T[], chunkSize: number): Array<Array<T>> {
  const res = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
}

async function spacesFetcher(...urls: string[]) {
  async function fetchSpaces(url: string) {
    const res = await fetch(url);

    const body = await res.json();

    if (res.status >= 200 && res.status < 300) {
      return body;
    }

    throw new APIError(body.message);
  }

  let spaces: Space[];

  const data = await Promise.all(urls.map((url) => fetchSpaces(url)));

  spaces = data.flat().map(toSpace);

  return spaces;
}

interface SWROptions {
  revalidateOnFocus?: boolean;
}

const SPACE_SIZE = 10;
/**
 * Fetch spaces and "guarantee" a response without `loading` boolean
 */
export function useSpacesQuery(
  spaceIDs: string[],
  swrOptions?: SWROptions
): Space[] {
  const { locale } = useI18n();

  const urls = useMemo(
    () =>
      sliceIntoChunks(spaceIDs, SPACE_SIZE).map(
        (ids) =>
          `/api/v2/spaces${toSpacesSearchParams({
            locale: locale.languageTag,
          })}&ids=${ids.join(",")}`
      ),
    [spaceIDs, locale.languageTag]
  );
  const options = useMemo(() => {
    const result: LiteralObject = { suspense: true };
    if (swrOptions && !isUndefined(swrOptions.revalidateOnFocus)) {
      result.revalidateOnFocus = swrOptions.revalidateOnFocus;
    }

    return result;
  }, [swrOptions]);

  const response = useSWR(
    isNotEmpty(urls) ? urls : null,
    spacesFetcher,
    options
  );

  const [cache, setCache] = useState(response.data || []);

  useEffect(() => {
    if (response.data) {
      setCache(response.data);
    }
  }, [response.data]);

  return cache;
}

export function useSpacesQueryCallback() {
  const get = useGet();
  const { locale } = useI18n();

  return async (spaceIDs: string[]): Promise<LightSpace[]> => {
    const response = await get<unknown[]>(
      `/api/v2/spaces${toSpacesSearchParams({
        locale: locale.languageTag,
      })}&light_response=true&ids=${spaceIDs.join(",")}`
    );

    return response.map(toLightSpace);
  };
}

export function usePartnerOfferingSpacesQueryCallback() {
  const get = useGet();
  const { locale } = useI18n();

  return async (
    partnerOfferingID: string,
    city: string,
    params?: PartnerOfferingParams
  ): Promise<LightSpace[]> => {
    let searchParams = new URLSearchParams();
    let queryString = "";

    if (params) {
      if (params.state) {
        searchParams.set("state", params.state);
      }
      if (params.city) {
        searchParams.set("city", params.city);
      } else {
        searchParams.set("city", city);
      }
      if (params.locationID) {
        searchParams.set("location_id", params.locationID);
      }
      if (params.minCapacity) {
        searchParams.set("min_capacity", String(params.minCapacity));
      }
      if (params.maxCapacity) {
        searchParams.set("max_capacity", String(params.maxCapacity));
      }
      if (params.startDate) {
        searchParams.set("start_date", params.startDate);
      }
      if (params.endDate) {
        searchParams.set("end_date", params.endDate);
      }
      if (params.date) {
        searchParams.set("date", params.date);
      }
      if (params.startTime && params.endTime) {
        searchParams.set("start_time", params.startTime);
        searchParams.set("end_time", params.endTime);
      }

      queryString = `?${searchParams.toString()}`;
    }

    if (queryString) {
      queryString += `&locale=${locale.languageTag}`;
    } else {
      queryString += `?locale=${locale.languageTag}`;
    }

    const response = await get<{ spaces: unknown[] }>(
      `/api/v2/partner_offerings/${partnerOfferingID}/spaces${queryString}`
    );

    return response.spaces.map(toLightSpace);
  };
}

/**
 * Fetch spaces but also return `loading` boolean if it is fetching
 */
export function useSpacesWithLoadingQuery(spaceIDs: string[]) {
  const { locale } = useI18n();

  const response = useSWR(
    isNotEmpty(spaceIDs)
      ? `/api/v2/spaces?ids=${spaceIDs.join(",")}&locale=${locale.languageTag}`
      : null
  );

  return {
    data: (response.data ? response.data.map(toSpace) : []) as Space[],
    loading: isNotEmpty(spaceIDs) && !response.data,
  };
}

interface LocationPhysicalSpacesParams {
  startDate?: string;
  endDate?: string;
  minCapacity?: number;
  maxCapacity?: number;
  city?: string;
}

export function useLocationPhysicalSpacesQuery(
  locationID: string,
  params: LocationPhysicalSpacesParams
): LocationPhysicalSpace[] {
  let searchParams = new URLSearchParams();
  let qs = "";

  if (params) {
    if (params.startDate) {
      searchParams.set("start_date", params.startDate);
    }
    if (params.endDate) {
      searchParams.set("end_date", params.endDate);
    }
    if (params.minCapacity) {
      searchParams.set("min_capacity", String(params.minCapacity));
    }
    if (params.maxCapacity) {
      searchParams.set("max_capacity", String(params.maxCapacity));
    }
    if (params.city) {
      searchParams.set("city", params.city);
    }

    qs = `${searchParams.toString()}`;
  }

  const response = useSWR(
    `/api/v2/locations/${locationID}/physical_spaces?space_type=private_office&${qs}`,
    { suspense: true }
  );

  return response.data.map(toLocationPhysicalSpace);
}

function toLocationPhysicalSpace(data: any): LocationPhysicalSpace {
  return {
    endDate: data.end_date,
    floor: data.floor,
    id: data.id,
    name: data.name,
    spaceID: data.space_id,
    startDate: data.start_date,
    withWindow: data.with_window,
    space: {
      layouts: data.space.space_layouts,
      pricings: data.space.pricings.map((p: any) => ({
        price: p.price,
        type: p.pricing_type,
      })),
    },
  };
}
interface LocationPhysicalSpace {
  endDate: string;
  floor: string;
  id: string;
  name: string;
  spaceID: string;
  startDate: string;
  withWindow: string;
  space: {
    layouts: SpaceLayout[];
    pricings: Pricing[];
  };
}

export interface PhysicalSpace {
  id: string;
  name: string;
  floor: string;
  spaceID: string;
}

function toPhysicalSpace(data: any): PhysicalSpace {
  return {
    id: data.id,
    name: data.name,
    floor: data.floor,
    spaceID: data.space_id,
  };
}

/**
 * @deprecated use `useLocationV2Query` with suspense instead
 */
export function useLocationQuery(locationID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  // If the key is null, SWR returns the latest data. Refer: https://github.com/vercel/swr/issues/1345
  const { data, error } = useSWR(
    locationID ? `/api/v2/locations/${locationID}${qs}` : null
  );

  const locationData = data && locationID ? toLocation(data) : undefined;

  if (locationData && locationData.googleAction?.entityId) {
    Cookies.set("merchant_id", locationData.googleAction.entityId, {
      expires: 30,
    });
  }

  return {
    data: locationData,
    loading: locationID && data === undefined,
    error,
  };
}

// Force-fetch the English location name regardless of the user's UI locale —
// used for analytics dimensions that should stay consistent across viewers.
export function useEnglishLocationName(
  locationID: string | undefined,
  enabled: boolean
): string | undefined {
  const { data } = useSWR(
    enabled && locationID
      ? `/api/v2/locations/${locationID}?locale=en-US`
      : null
  );
  return data?.name;
}

export function useAllLocationsQuery(partnerID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  // If the key is null, SWR returns the latest data. Refer: https://github.com/vercel/swr/issues/1345
  const { data, error } = useSWR(
    partnerID ? `/api/v2/partners/${partnerID}/locations${qs}` : null
  );

  return {
    data:
      data?.locations && partnerID ? data.locations.map(toLocation) : undefined,
    loading: partnerID && data === undefined,
    error,
  };
}

export function useGetAvailableDaysMembership(membershipId?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  // If the key is null, SWR returns the latest data. Refer: https://github.com/vercel/swr/issues/1345
  const { data, error } = useSWR<string[], any>(
    membershipId
      ? `/api/v2/memberships/${membershipId}/available_days${qs}`
      : null
  );

  return {
    data: data && membershipId ? data : undefined,
    loading: membershipId && data === undefined && !error,
    error,
  };
}

export interface MembershipPolicies {
  membershipPlanID: string;
  cancellationText: string;
  terms?: string;
  termsRequired: boolean;
  askForTaxNumber?: boolean;
  requireTaxId?: boolean;
  requireCompany?: boolean;
  requireLastName?: boolean;
  requireBillingState?: boolean;
  marketingConsent?: string;
  marketingConsentCheckedByDefault: boolean;
  signedContractRequired: boolean;
  contractVersionID?: string;
  contractURL?: string;
  consentText?: string;
}

export function useMembershipPoliciesQuery(offeringItemID?: string): {
  data?: MembershipPolicies;
  loading: boolean;
  error: any;
  mutate: () => Promise<unknown>;
} {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  // If the key is null, SWR returns the latest data. Refer: https://github.com/vercel/swr/issues/1345
  const { data, error, mutate } = useSWR(
    offeringItemID
      ? `/api/v2/partner_offering_items/${offeringItemID}/policies${qs}`
      : null
  );

  return {
    data:
      data && offeringItemID
        ? {
            membershipPlanID: data.membership_plan_id,
            cancellationText: data.cancellation_text,
            terms: data.terms,
            termsRequired: data.terms_required,
            askForTaxNumber: data.ask_for_tax_number,
            requireTaxId: data.require_tax_id,
            requireCompany: data.require_company ?? false,
            requireLastName: data.require_last_name ?? false,
            requireBillingState: data.require_billing_state ?? false,
            marketingConsent: data.marketing_consent,
            marketingConsentCheckedByDefault:
              data.marketing_consent_checked_by_default,
            signedContractRequired: !!data.signed_contract_required,
            contractVersionID: data.contract_version_id ?? undefined,
            contractURL: data.contract_url ?? undefined,
            consentText: data.consent_text ?? undefined,
          }
        : undefined,
    loading: !!offeringItemID && data === undefined,
    error,
    mutate,
  };
}

export function useLocationV2Query(locationID: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(`/api/v2/locations/${locationID}${qs}`, {
    suspense: true,
  });

  const location = toLocation(data);
  if (location.googleAction?.entityId) {
    Cookies.set("merchant_id", location.googleAction.entityId, { expires: 30 });
  }
  return location;
}

export function useLocationQueryMaybe(locationID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data } = useSWR(
    locationID ? `/api/v2/locations/${locationID}${qs}` : null,
    {
      suspense: true,
    }
  );

  return data ? toLocation(data) : undefined;
}

export function usePartnerLocationsQuery(partnerID: string): PartnerLocation[] {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const response = useSWR(`/api/v2/partners/${partnerID}/locations${qs}`, {
    suspense: true,
  });

  return response.data.success
    ? response.data.locations.map(toPartnerLocation)
    : [];
}

function toPartnerLocation(data: any): PartnerLocation {
  return {
    id: data.id,
    name: data.name,
    timezone: data.time_zone,
    wlHomeLink: data.wl_home_link,
    country: data.country,
    city: data.city,
    state: data.state,
    stateName: data.state_name,
    streetAddress: data.street_address,
    postalCode: data.postal_code,
    direction: data.direction,
    longitude: data.longitude,
    latitude: data.latitude,
    contactPhone: data.contact_phone,
    phoneNumberMask: data.phone_number_mask,
  };
}

function useLocationPartnerIntegrations(
  locationID?: string
): ResourceIntegration[] | undefined {
  // Non-suspense: the only consumer is useTranzilaLocationPartnerIntegration,
  // which already returns `undefined` while loading. Making this suspending
  // caused checkout to wipe to the App-level Suspense fallback the first time
  // the integrations endpoint hit a cold cache (e.g. Stripe-only partners
  // where the integration data isn't actually needed).
  const { data } = useSWR(
    locationID ? `/api/v2/locations/${locationID}/partner_integrations` : null
  );

  return data?.map(toResourceIntegration);
}

export function useTranzilaLocationPartnerIntegration(
  locationID?: string
): ResourceIntegration | undefined {
  const integrations = useLocationPartnerIntegrations(locationID);
  return integrations?.find(
    (i) =>
      i.integrationType.toLowerCase() === IntegrationType.TRANZILA.toLowerCase()
  );
}

export function toDynamicPriceRate(data: any): DynamicPriceRate {
  return {
    id: data?.id,
    adjustmentType: data?.adjustment_type,
    adjustmentValue: data?.adjustment_value,
    description: data?.description,
    displayDescription: data?.display_description,
    orderNonRefundable: data?.order_non_refundable,
  };
}

function toOrderItemPrice(data: any): OrderItemPrice {
  return {
    startDate: data.start_date,
    endDate: data.end_date,
  };
}

// 1-based and stable; anything non-positive is treated as absent rather than
// rendered as a "#0" row.
function toOccurrenceIndex(value: unknown): number | undefined {
  const index = Number(value);
  return Number.isInteger(index) && index > 0 ? index : undefined;
}

// `Booking.status` on the API: a row is created, and stays so until cancelled.
export type BookingLineStatus = "created" | "canceled";

function toBookingLineStatus(value: unknown): BookingLineStatus | undefined {
  return value === "created" || value === "canceled" ? value : undefined;
}

export function toOrderItem(data: any): OrderItem {
  const type: OrderableType = data.type || data.orderable_type;

  const base = {
    ID: data.id,
    orderableID: data.orderable_id,
    orderID: data.order_id,
    orderItemID: data.order_item_id,
    locationID: data.location_id,
    orderConfirmationID: data.order_confirmation_id,
    orderName: data.order_name,
    status: data.order_status,
    quantity: data.quantity,
    orderTotalPrice: data.order_total_price,
    subscriptionFirstPaymentAmount: data.subscription_first_payment_amount,
    orderEmail: data.order_email,
    orderCurrency: data.order_currency,
    productType: {
      id: data.product_type?.id,
      name: data.product_type?.name,
      category: data.product_type?.category,
    },
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
    originalUnitPrice: data.original_unit_price,
    originalSubTotal: data.original_sub_total,
    createdAt: new Date(data.created_at * 1000),
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    orderItemPrices: data.order_item_prices?.map(toOrderItemPrice),
    parentLineItemId: data.parent_line_item_id ?? null,
    bookingSeriesId: data.booking_series_id ?? undefined,
    occurrenceIndex: toOccurrenceIndex(data.occurrence_index),
    // The Booking row's own status rides under `orderable`; the line has none
    // of its own on this payload.
    bookingStatus: toBookingLineStatus(data.orderable?.status),
    cancellableWithoutRefund:
      typeof data.details?.cancellable_without_refund === "boolean"
        ? data.details.cancellable_without_refund
        : undefined,
    feeLines: data.fee_lines?.map(toFeeLine),
    spaceOfferingDate: data.space_offering_date ?? undefined,
  };

  if (type === OrderableType.AddOn) {
    return {
      ...base,
      type,
      details: toOrderItemMultiPassDetails(data.details),
    };
  }

  if (type === OrderableType.Membership) {
    return {
      ...base,
      inventoryExceededBy: data.inventory_exceeded_by,
      type: OrderableType.Membership,
    };
  }

  if (data.details?.start_time) {
    return {
      ...base,
      type,
      details: toOrderItemHourlyPassDetails(data.details),
    };
  }

  if (data.details?.time) {
    return {
      ...base,
      type,
      details: toLegacyOrderItemHourlyPassDetails(data.details),
    };
  }

  return {
    ...base,
    type,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    inventoryExceededBy: data?.inventory_exceeded_by,
    details: toOrderItemDayPassDetails(data.details),
  };
}

export function toQuoteItem(data: any): QuoteItem {
  return {
    orderableType: data.orderable_type,
    quoteId: data.quote_id,
    orderableId: data.orderable_id,
    orderable: toOrderable(data.orderable),
    originalUnitPrice: data.original_unit_price,
    unitPrice: data.unit_price,
    totalPrice: toNumber(data.total_price),
    quantity: data.quantity,
    date: data.date,
    bundled: data.bundled,
    subTotal: data.sub_total,
    unitCount: data.unit_count,
    startDate: data.start_date,
    endDate: data.end_date,
    days: data.days,
    startTime: data.start_time,
    endTime: data.end_time,
    unitPriceAfterDiscount: data.unit_price_after_discount,
    unitPriceAfterDiscountWithTax: data.unit_price_after_discount_with_tax,
    unitPriceWithTax: data.unit_price_with_tax,
    unitPriceAfterDiscountWithTaxAndFee:
      data.unit_price_after_discount_with_tax_and_fee,
    unitPriceWithTaxAndFee: data.unit_price_with_tax_and_fee,
    originalTotalPrice: data.original_total_price,
    originalSubTotalPrice: data.original_sub_total,
    quoteItemPrices: data.quote_item_prices.map(toQuoteItemPrice),
    promotionFreeUnit: data.promotion_free_unit,
    promotionAmount: data.promotion_amount,
    promotion: data.promotion ? toPromotion(data.promotion) : undefined,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
    feeLines: data.fee_lines?.map(toFeeLine),
    taxAmount: data.tax_amount,
    taxDescription: data.tax_description,
    taxPercentage: data.tax_percentage,
    spaceOfferingDate: data.space_offering_date,
    parentLineItemId: data.parent_line_item_id ?? null,
    bookingSeriesId: data.booking_series_id ?? undefined,
    occurrenceIndex: data.occurrence_index ?? undefined,
    dailyRateApplied: data.daily_rate_applied ?? false,
  };
}

function toOrderItemMultiPassDetails(data: any): OrderItemMultiPassDetails {
  return {
    type: data?.type,
    addOnType: data?.add_on_type,
    pricingType: data?.pricing_type,
    spaceID: data?.space_id,
    addOnID: data?.add_on_id,
    name: data?.add_on_name,
    description: data?.add_on_description,
    numberOfEntries: data?.number_of_entries,
    daysToExpire: data?.days_to_expire,
  };
}

function toOrderItemDayPassDetails(data: any): OrderItemDayPassDetails {
  return {
    type: BookingType.Daily,
    attendees: data?.attendees,
    layoutID: data?.space_layout_id,
    spaceID: data?.space_id,
    startDate: data?.start_date,
    endDate: data?.end_date,
    days: data?.days,
    physicalSpace: data?.physical_space,
    physicalSpaceID: data?.physical_space_id,
    accessID: data?.access_id,
    accessName: data?.access_name,
    accessNumberOfEntries: data?.access_number_of_entries,
  };
}

function toOrderItemHourlyPassDetails(data: any): OrderItemHourlyPassDetails {
  return {
    type: BookingType.Hourly,
    spaceID: data?.space_id,
    date: data?.date,
    hours: data?.hours,
    startTime: data?.start_time,
    endTime: data?.end_time,
    layoutID: data?.space_layout_id,
    attendees: data?.attendees,
    physicalSpaceID: data?.physical_space_id,
    physicalSpace: data?.physical_space,
  };
}

function toLegacyOrderItemHourlyPassDetails(
  data: any
): OrderItemHourlyPassDetails {
  return {
    type: BookingType.Hourly,
    spaceID: data?.space_id,
    date: data?.date,
    hours: data?.hours,
    startTime: data?.time,
    endTime: addHours(data?.time, data?.hours),
  };
}

export function useGetPartnerProductTypes(partnerID: string) {
  const response = useSWR(`/api/v2/partners/${partnerID}/product_types`, {
    suspense: false,
  });

  const data: ProductType[] = useMemo(
    () => (response.data ? response.data.map(toProductType) : []),
    [response.data]
  );
  return {
    data,
    loading: response.data === undefined,
  };
}

function toProductType(data: any): ProductType {
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    invites: data.invites,
    selectQuantity: data.select_quantity,
    spaceTypeForAddOn: data.space_type_for_add_on,
  };
}

export interface Partner {
  id: string;
  name: string;
  timeZone: string;
  partnerSlug: string;
  phoneNumber: string;
  logoUrl: string;
  internalName?: string;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  tiktok_url?: string;
}

// The slug is optional because the portal's fixed auth paths carry none: with no
// partner in the path, query string, or storage, callers legitimately have
// nothing to pass, and the request 404s.
export function usePartnerQuery(partnerSlug: string | undefined): Partner {
  const response = useSWR(`/api/v2/partners/${partnerSlug}`, {
    suspense: true,
  });

  return {
    id: response.data.id,
    name: response.data.name,
    timeZone: response.data.time_zone,
    partnerSlug: response.data.partner_slug,
    phoneNumber: response.data.phone_number,
    logoUrl: response.data.logo_url,
    internalName: response.data.internal_name,
    facebook_url: response.data.facebook_url,
    instagram_url: response.data.instagram_url,
    twitter_url: response.data.twitter_url,
    linkedin_url: response.data.linkedin_url,
    tiktok_url: response.data.tiktok_url,
  };
}

export function usePartnerCatalogQueryMaybe(
  partnerSlug: string,
  catalogSlug?: string
) {
  const response = useSWR(
    catalogSlug ? `/api/v2/partner_catalogs?partner_slug=${partnerSlug}` : null,
    {
      suspense: false,
    }
  );

  return useMemo(
    () =>
      response.data
        ? response.data
            .map((catalog: any) => toPartnerCatalog(catalog))
            .find((pc: any) => pc.slug === catalogSlug)
        : null,
    [catalogSlug, response.data]
  );
}

export function usePartnerCatalogsQuery(
  partnerSlug: string,
  config: ConfigInterface = { suspense: true },
  options: CatalogMapOptions = {}
): PartnerCatalog[] {
  const { locale } = useI18n();

  const response = useSWR(
    `/api/v2/partner_catalogs?partner_slug=${partnerSlug}&locale=${locale.languageTag}`,
    config
  );

  return response.data?.map((catalog: any) =>
    toPartnerCatalog(catalog, options)
  );
}

export function usePartnerCategoryQuery(
  categorySlug: string
): PartnerCatalogCategory | undefined {
  const { locale } = useI18n();

  const response = useSWR(
    `/api/v2/partner_catalog_categories/slugs/${categorySlug}?locale=${locale.languageTag}`,
    { suspense: true }
  );

  return response.data ? toPartnerCatalogCategory(response.data) : undefined;
}

export interface PaymentIntent {
  currency: string;
  paymentId: string;
  proratedAmount: number;
  stripeCustomerId: string;
  paymentClientSecret: string;
  stripeSubsciptionId: string;
  firstPaymentAmount: number;
}

export interface Promotion {
  code: string;
  description: string;
  longDescription: string;
}

export function usePartnerPromotionsQuery(
  partnerID: string,
  promotableType: string,
  promotableID?: string
) {
  const { data, error } = useSWR(
    promotableID
      ? `/api/v2/partners/${partnerID}/promotions?promotable_type=${promotableType}&promotable_id=${promotableID}`
      : null,
    {
      suspense: false,
    }
  );

  return {
    data: (data
      ? data.map((data: any) => ({
          code: data.code,
          description: data.description,
          longDescription: data.long_description,
        }))
      : []) as Promotion[],
    error,
    loading: promotableID && !data,
  };
}

export interface QuoteInput {
  source: OrderSource;
  partnerID: string;
  promoCode: string;
  orderItems: OrderItemInput[];
  email?: string;
  quoteId?: string;
  partnerOfferingID?: string;
}

export type OrderItemInput =
  | OrderItemDayPassInput
  | OrderItemMultiPassInput
  | OrderItemHourlyPassInput
  | OrderItemMembershipInput;

export interface OrderItemDayPassInput {
  quantity?: number;
  type: OrderableType.Booking;
  details: {
    type: BookingType.Daily;
    spaceID: string;
    startDate: string;
    endDate: string;
    bookingDates?: string[];
    physicalSpaceID?: string;
    eventTypeId?: string;
    eventTypeDescription?: string;
    // A daily recurring booking: one single-day booking per date. Meeting rooms
    // only; the server refuses it anywhere else.
    recurrence?: RecurrenceInput;
    legacy__details: {
      layoutID: string;
      attendees: number;
    };
  };
  spaceOfferings?: AddOnOrderParams[];
}

export interface OrderItemMembershipInput {
  details: {
    membershipID: string;
    subscriptionInterval: string;
    subscriptionStartDate?: string;
  };
}

export interface OrderItemHourlyPassInput {
  type: OrderableType.Booking;
  // Day passes book a team's worth of passes for one window, so hourly order items
  // carry quantity (day offices send undefined — a single unit per booking).
  quantity?: number;
  details: {
    type: BookingType.Hourly;
    spaceID: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    startDate?: string;
    endDate?: string;
    eventTypeId?: string;
    eventTypeDescription?: string;
    // An hourly recurring booking: the same window on every date. Meeting rooms
    // only; the server refuses it anywhere else.
    recurrence?: RecurrenceInput;
    legacy__details: {
      layoutID: string;
      attendees: number;
    };
  };
  spaceOfferings?: AddOnOrderParams[];
}

export interface OrderItemMultiPassInput {
  type: OrderableType.AddOn;
  details: {
    type: AddOnType.MultiPass;
    spaceID: string;
    addOnID: string;
    spaceOfferingID: string;
    capacity?: number;
  };
}

export interface TaxLine {
  amount: number;
  percentage: number;
  description: string;
}

// How long a membership promo discounts: N billing cycles, through an end-month, or the whole
// membership. A forever promo has no iterations and no end date, the same as no promo at all, so
// this is the only field that tells the two apart.
export type SubscriptionDiscountDuration = "iterations" | "until" | "forever";

export interface PromotionLine {
  amount: number;
  discount: number;
  itemPriceAfterDiscount: number;
  discountType: DiscountType;
  description: string;
  code: string;
  subscriptionIterations?: number;
  subscriptionPromotionEndsAt?: number;
  subscriptionDiscountDuration?: SubscriptionDiscountDuration;
  sponsorPayment?: {
    enabled: boolean;
    message?: string | null;
  };
}

export interface FeeLine {
  displayName: string;
  calculatedAmount: number;
  id: string;
  displayInformation?: string;
}

// The recurring booking's rule and span, stated once by the quote so the
// storefront reads it here instead of re-deriving it from N lookalike quote
// items. Per-date prices stay on the items (ordered by `occurrenceIndex`).
export interface QuoteRecurrence {
  bookingSeriesId: string;
  frequency: string;
  interval: RecurrenceInterval;
  weekday: number;
  count: number;
  dates: string[];
  // Pattern dates inside the span that were unavailable at quote and so were
  // not booked — the calendar event and the .ics exclude exactly these.
  skippedDates: string[];
  firstDate: string;
  lastDate: string;
  priceMin: number;
  priceMax: number;
  priceTotal: number;
  /**
   * The last moment the whole recurring booking can be cancelled for a full
   * refund — the first date's 100%-tier cutoff, served by the quote because
   * checkout has no order to read `order.recurringBooking.refundableUntil`
   * from. ISO 8601.
   *
   * Absent where the room never offers a full refund, and on a pre-deploy
   * response that predates the field: both mean the checkout discloses a
   * booking that cannot be refunded, which is the safe reading of a missing
   * deadline either way.
   */
  refundableUntil?: string;
}

export function toQuoteRecurrence(data: any): QuoteRecurrence | null {
  if (!data?.booking_series_id) return null;
  return {
    bookingSeriesId: data.booking_series_id,
    frequency: data.frequency,
    // Weekly is the only preset shipped; anything else reads as it.
    interval: isRecurrenceInterval(data.interval) ? data.interval : 1,
    weekday: data.weekday,
    count: data.count,
    dates: data.dates ?? [],
    skippedDates: data.skipped_dates ?? [],
    firstDate: data.first_date,
    lastDate: data.last_date,
    priceMin: toNumber(data.price_min),
    priceMax: toNumber(data.price_max),
    priceTotal: toNumber(data.price_total),
    refundableUntil: data.refundable_until ?? undefined,
  };
}

export interface Quote {
  id: string;
  totalPrice: number;
  subTotal: number;
  subTotalBeforeDiscount: number;
  available?: boolean;
  orderItems: OrderItem[];
  quoteItems: QuoteItem[];
  // Present only on a recurring booking's quote. Null (or absent on a pre-deploy
  // response) means an ordinary quote.
  recurrence?: QuoteRecurrence | null;
  taxLine: TaxLine | null;
  promotionLine: PromotionLine | null;
  paymentClientSecret?: string | null;
  paymentIntent: PaymentIntent;
  currency: string;
  feeLines?: FeeLine[];
  passPaymentMethodToPms?: boolean;
  // Time-accurate Request-to-Book decision for the selected booking time
  // (static policy or after-hours window), from the v2 quote serializer.
  // Optional so a pre-deploy API response falls back to the static policy flag.
  requestToBook?: boolean;
}

export interface DynamicPriceRate {
  adjustmentType: DynamicPricingAdjustmentType;
  adjustmentValue: number;
  // The internal admin label. Staff-facing surfaces render this; checkout must
  // not — see displayDescription.
  description: string;
  // The customer-facing label, already resolved server-side to fall back to the
  // internal description. Optional because not every rate payload carries it.
  displayDescription?: string;
  id: string;
  orderNonRefundable: boolean;
}

interface OrderItemPrice {
  startDate: string;
  endDate: string;
}

interface OrderItemBase {
  ID: string;
  quantity: number;
  orderableID: string;
  orderID: string;
  orderItemID: string;
  locationID: string;
  orderConfirmationID: string;
  orderName: string;
  orderCurrency: string;
  orderEmail: string;
  orderTotalPrice: number;
  subscriptionFirstPaymentAmount: number;
  productType: {
    id: string;
    name: string;
    category?: string;
  };
  dynamicPriceRate?: DynamicPriceRate;
  originalUnitPrice: number;
  originalSubTotal: number;
  unitPrice: number;
  subTotal: number;
  status: OrderStatus;
  createdAt: Date;
  orderItemPrices?: OrderItemPrice[];
  parentLineItemId?: string | null;
  // Set on every primary line of a recurring booking; the index is 1-based,
  // assigned in date order at quote and never renumbered.
  bookingSeriesId?: string;
  occurrenceIndex?: number;
  // The booked row's live status, for a Booking line. `status` above is the
  // order's, when the payload carries it at all.
  bookingStatus?: BookingLineStatus;
  // Whether this one booking can still be cancelled while the order stays paid —
  // live, not started, and not held by its workspace system's own fee. The
  // manage-order release endpoint judges the same thing; this only decides
  // whether to offer the action. Absent on a payload predating the field.
  cancellableWithoutRefund?: boolean;
  // The fees charged on this line. A fee is priced per line, so a recurring
  // booking carries the same fee once per date rather than once per order.
  feeLines?: FeeLine[];
  // The date this line was sold for, on a line that is sold per date. An add-on
  // priced per day carries one line per date, and this is what tells them
  // apart; a booking line prices its own span and leaves it null.
  spaceOfferingDate?: string;
}

export interface OrderItemDayPass extends OrderItemBase {
  type: OrderableType.Booking;
  inventoryExceededBy: number;
  details: OrderItemDayPassDetails;
}

export interface OrderItemHourlyPass extends OrderItemBase {
  type: OrderableType.Booking;
  details: OrderItemHourlyPassDetails;
}

interface OrderItemHourlyPassDetails {
  type: BookingType.Hourly;
  date: string;
  hours: number;
  startTime: string;
  endTime: string;
  spaceID: string;
  attendees?: number;
  accessID?: string;
  accessName?: string;
  accessNumberOfEntries?: string;
  layoutID?: string;
  // Optional because quote responses (pre-creation) omit it — the allocator
  // assigns the unit only once the booking is created.
  physicalSpaceID?: string;
  physicalSpace?: Pick<PhysicalSpace, "id" | "name" | "floor">;
}

interface OrderItemDayPassDetails {
  type: BookingType.Daily;
  startDate: string;
  endDate: string;
  spaceID: string;
  layoutID: string;
  days: number;
  physicalSpaceID?: string;
  physicalSpace?: Pick<PhysicalSpace, "id" | "name" | "floor">;
  attendees?: number;
  accessID?: string;
  accessName?: string;
  accessNumberOfEntries?: string;
}

export enum AddOnType {
  MultiPass = "MultiPass",
}

/**
 * The `addOnType` a Multi Pass carries. Ordinary booking add-ons — coffee,
 * catering, AV — are `OrderableType.AddOn` items too, so this field is the only
 * thing that tells the two apart.
 */
export const ACCESS_ADD_ON_TYPE = "access_add_on";

export enum AddonPricingType {
  Guest = "guest_per_day",
  Quantity = "quantity_per_day",
  Capacity = "capacity_per_day",
  Fixed = "fixed",
  Flat = "flat_per_day",
  FlatPerBooking = "flat_per_booking",
  QuantityPerBooking = "quantity_per_booking",
}

export const productDetailAddonTypes = [
  AddonPricingType.Guest,
  AddonPricingType.Quantity,
  AddonPricingType.Capacity,
  AddonPricingType.Flat,
  AddonPricingType.FlatPerBooking,
  AddonPricingType.QuantityPerBooking,
];

// Capacity and Flat both bill as a single per-day, whole-space total: no
// per-guest/per-item quantity stepper, no `$unit × N` line in breakdowns.
// They differ only in how the total is derived (capacity multiplies by the
// space's layout capacity; flat uses the offering price as the total).
export function isWholeSpacePricing(
  pricingType: AddonPricingType | undefined
): boolean {
  return (
    pricingType === AddonPricingType.Capacity ||
    pricingType === AddonPricingType.Flat
  );
}

// FlatPerBooking and QuantityPerBooking charge once for the whole booking
// regardless of duration. Caller-side day loops must collapse to a single
// entry, and display surfaces must drop the `× N days` suffix.
export function isPerBookingPricing(
  pricingType: AddonPricingType | undefined
): boolean {
  return (
    pricingType === AddonPricingType.FlatPerBooking ||
    pricingType === AddonPricingType.QuantityPerBooking
  );
}

// Quantity-based add-ons need a stepper control and a per-unit quantity in
// the breakdown / payload. Covers both per-day and per-booking variants.
// Module-internal: only isStepperPricing composes it now.
function isQuantityBasedPricing(
  pricingType: AddonPricingType | undefined
): boolean {
  return (
    pricingType === AddonPricingType.Quantity ||
    pricingType === AddonPricingType.QuantityPerBooking
  );
}

// Every pricing model the booker adjusts through a stepper, and so the only
// ones a configured minimum can bind on. Deliberately wider than
// isQuantityBasedPricing, which excludes Guest — collapsing the two would strip
// the stepper from every guest_per_day add-on.
export function isStepperPricing(
  pricingType: AddonPricingType | undefined
): boolean {
  return (
    pricingType === AddonPricingType.Guest ||
    isQuantityBasedPricing(pricingType)
  );
}

// The stepper models billed per day: a stepper that is not a per-booking
// stepper. These are the only add-ons that cross a per-day floor on the first
// "+", since per-booking steppers charge once for the whole booking and so have
// no per-day floor to word. This is the set whose card shows a
// "From $X/day (min N/day)" entry line.
export function isPerDayStepperPricing(
  pricingType: AddonPricingType | undefined
): boolean {
  return isStepperPricing(pricingType) && !isPerBookingPricing(pricingType);
}

// Flat-rate add-ons (per-day or per-booking) have an implicit quantity of 1.
export function isFlatPricing(
  pricingType: AddonPricingType | undefined
): boolean {
  return (
    pricingType === AddonPricingType.Flat ||
    pricingType === AddonPricingType.FlatPerBooking
  );
}

// Builds the single `space_offerings_params` entry for a per-booking add-on.
// FlatPerBooking ignores quantity; QuantityPerBooking uses the user-supplied
// count. Caller passes the booking's first date as the entry's `date`.
export function buildPerBookingAddOnEntry(
  offering: Pick<SpaceOffering, "id" | "pricingType">,
  date: string,
  quantity: number
): { spaceOfferingID: string; quantity: number | undefined; date: string } {
  return {
    spaceOfferingID: offering.id,
    quantity:
      offering.pricingType === AddonPricingType.QuantityPerBooking
        ? quantity
        : undefined,
    date,
  };
}

export interface SpaceOffering {
  id: string;
  name: string;
  displayName?: string;
  description: string;
  shortDescription?: string;
  price: number;
  addOnID: string;
  productCategory: string;
  productTypeName: string;
  spaceID: string;
  numberOfEntries?: number;
  daysToExpire?: number;
  sortOrder?: number | null;
  credits?: number | null;
  pricingType?: AddonPricingType;
  // Smallest quantity this offering may be ordered in, configured per offering
  // because the unit it counts is whatever its own pricingType defines. No
  // minimum arrives as `null` (the serializer always emits the key) and as
  // `undefined` on offerings synthesized locally — read it through `??`, never
  // an `=== undefined` test. The server only ever stores values above 1, and
  // only on quantity-bearing pricing types.
  minQuantity?: number | null;
  // The add-on's own unit noun, already localized by the server and shown in
  // place of the pricingType default (see getPricingUnit). It exists because
  // the unit belongs to what is being sold, not to the billing rule: coffee
  // priced per person and printing priced per page share one pricingType, so
  // no per-type label can be right for both. Unset on most add-ons, which keep
  // the pricingType wording.
  priceUnit?: string | null;
  iconURL?: string;
  // Photos of the add-on itself, shown in the details modal. The API always
  // sends an array, but this type also describes offerings synthesized locally
  // (multi-pass and day-pass pseudo-offerings), which carry no imagery.
  images?: ImageVersion[];
  // Staff-only add-on: hidden from and non-purchasable on the customer checkout,
  // appliable only from the FM Dashboard. The server already excludes these from
  // the customer response; this field backs a defensive client-side filter.
  dashboardOnly?: boolean;
  // Per requested parent booking date, whether the add-on can be sold that day.
  // Present only for gated add-ons when the offerings request supplied
  // parent_booking_dates.
  availabilityByDate?: SpaceOfferingDateAvailability[];
}

// One requested booking date's verdict on an add-on offering.
export interface SpaceOfferingDateAvailability {
  date: string;
  // Whether the day clears the add-on's supply gates — desk inventory and its
  // lead-time cutoff. The server may fold its own daily-capacity verdict in
  // here too, so a false value does not say which gate closed; read
  // `remainingUnits` when the reason has to be named.
  available: boolean;
  // Units of this add-on still sellable at the location on `date`, under its
  // configured daily capacity. `null` means no capacity is configured, so the
  // day is uncapped — never read it as zero. Optional rather than required
  // because offerings synthesized on the client carry no availability at all;
  // read it through `??`, never an `=== undefined` test.
  remainingUnits?: number | null;
}

export interface OrderItemMultiPass extends OrderItemBase {
  type: OrderableType.AddOn;
  details: OrderItemMultiPassDetails;
}

interface OrderItemMultiPassDetails {
  type: AddOnType.MultiPass;
  addOnType: string;
  // How the add-on was priced — per guest per day, per booking, whole-space.
  // It decides how the line reads back ("5/day · $13 each" vs "2 · $20 each"),
  // and it is what tells a booking add-on from a Multi Pass sharing its order,
  // which is priced `fixed` and belongs to none of the booking shapes.
  pricingType?: AddonPricingType;
  spaceID: string;
  addOnID: string;
  name: string;
  description: string;
  numberOfEntries: number;
  daysToExpire: number;
}

export interface OrderItemMembership extends OrderItemBase {
  type: OrderableType.Membership; // TODO: correct this orderItem Type on WL
  inventoryExceededBy: number;
}

export type OrderItem =
  | OrderItemDayPass
  | OrderItemHourlyPass
  | OrderItemMultiPass
  | OrderItemMembership;

export interface Order {
  id: string;
  currencyCode: string;
  subTotal: number;
  subTotalBeforeDiscount: number;
  subscriptionFirstPaymentAmount: number;
  totalPrice: number;
  totalPriceInUsd: number;
  stripeClientSecret: string;
  confirmationID: string;
  additionalNotes: string;
  status: OrderStatus;
  /**
   * Why the payment did not complete. Only meaningful alongside a
   * `PaymentFailed` status, and only ever set by the API — a decline carries
   * the gateway's own message, while the checkout keys its own copy off the
   * machine-readable values in `PaymentFailure`.
   */
  paymentFailedReason?: string;
  /**
   * When the failure the order is currently carrying happened. A retry
   * re-submits the same order, so this is the only thing separating a failure
   * the current attempt produced from one an earlier attempt left behind —
   * status and reason read identically in both cases. Only set while the order
   * is failed, and the API re-stamps it per attempt only when it records a
   * reason alongside, so an unchanged value means "no new failure was recorded"
   * rather than "no new failure happened".
   */
  paymentFailedAt?: string;
  orderItems: OrderItem[];
  // Fees charged on the order as a whole rather than on any one line — the
  // partner's own fee. Charged once however many dates the order covers, so it
  // is stated alongside the per-line fees on `orderItems`, never instead of them.
  feeLines?: FeeLine[];
  fullName: string;
  companyName: string;
  email: string;
  bookingReason: string;
  inviteID: string;
  phoneNumber: string;
  paidThrouTeamsSubscription: boolean;
  paymentMethodDetails: PaymentMethod | null;
  billingDetails: OrderBillingDetails | null;
  createdAt: Date;
  cancelledAt: string;
  taxLine: TaxLine | null;
  promotionLine: PromotionLine | null;
  source?: string;
  sourceLabel?: string;
  cancellable: boolean;
  uncancellableReason?: string;
  cancellationLockedUntil?: string | null;
  /**
   * The charge's remaining refundable BALANCE, before the booking's
   * cancellation tier is applied — not what a cancel would pay back. It is 0
   * on any order that is not paid. What a cancel returns is this figure times
   * `refundPercentage`: the same multiplication the backend performs at cancel
   * time (`Orders::PartialRefund`).
   */
  refundableAmount?: number;
  /**
   * What share of the refundable balance a cancel returns under the booking's
   * policy, 0-100. A tier, not an amount: multiply it by `refundableAmount`
   * for the payout. 0 whenever the cancel is refused outright.
   */
  refundPercentage?: number;
  /**
   * What has already been refunded across this booking's whole order chain —
   * the root order and every amendment child summed, so an FM amend to a
   * cheaper slot and a later customer cancel both count. `totalPrice` and `id`
   * describe only the latest child, so the two are not subtractable.
   */
  refundedAmount?: number;
  /**
   * Whether the customer may change this booking's date and time themselves.
   * Only the manage-order payload states it, so it reads as false everywhere
   * else — the absence of an answer is not permission.
   */
  amendable: boolean;
  /**
   * Why a change is refused, as a machine token (e.g. `cutoff_passed`,
   * `source_unsupported`). Set whenever `amendable` is false and the API ran the
   * check; the page decides from the token whether to word the refusal or hide
   * the action entirely.
   */
  notAmendableReason?: string;
  /** When the change window closes, ISO8601 in the location's zone. */
  changesAllowedUntil?: string;
  /**
   * The first order of this booking's amend chain. `id` is the latest order —
   * the one the manage token resolved to — so a receipt link uses `id`; a cancel
   * may post to either, the API resolves the chain from any member.
   */
  rootOrderId?: string;
  /** Where the customer adds this booking to their own calendar. Either may be absent. */
  calendarLinks?: { icsUrl?: string; googleUrl?: string };
  quoteId: string;
  currency: string;
  amendedOrders?: AmendedOrder[];
  specialInstructions?: string;
  // Present only on a recurring booking: the rule behind its booking lines.
  recurringBooking?: RecurringBooking;
}

interface AmendedOrder extends Order {
  orderItems: OrderItemHourlyPass[];
}

export interface CreditCard {
  last4: string;
  network: string;
  networkLogoUrl: string | null;
}

export interface PaymentMethod {
  card: CreditCard | null;
}

export enum OrderStatus {
  Created = "created",
  Paid = "paid",
  Canceled = "canceled",
  Confirmed = "confirmed",
  Accepted = "accepted",
  Chargeback = "chargeback",
  Rejected = "rejected",
  DisputePending = "dispute_pending",
  DisputeWon = "dispute_won",
  DisputeLost = "dispute_lost",
  PaymentFailed = "payment_failed",
  Requested = "requested",
}

interface CreateOrderInput {
  fullName: string;
  firstName?: string;
  lastName?: string;
  companyName: string;
  email: string;
  bookingReason?: string;
  locale: string;
  phoneNumber: string;
  partnerID: string;
  quoteID?: string;
  partnerOfferingID?: string;
  promoCode?: string;
  confirmWithoutPayment: boolean;
  marketingConsent: boolean;
  orderItems: OrderItemInput[];
  additionalNotes?: string;
  billing: OrderBillingDetails;
  // The checkout never asked for an address. The country is still pre-filled from
  // the location, and a country-only billing hash fails the API's billing-details
  // validation, so the key has to be left out of the request entirely.
  skipBillingDetails?: boolean;
  specialInstructions?: string;
  membershipNumber?: string;
  stripeCardToken?: string;
  contractSignature?: ContractSignatureInput;
  analytics?: {
    ga?: UTMParams;
  };
}

interface ContractSignatureInput {
  signerName: string;
  contractVersionID: string;
}

function fromContractSignatureInput(input?: ContractSignatureInput) {
  return input
    ? {
        signer_name: input.signerName,
        contract_version_id: input.contractVersionID,
      }
    : undefined;
}

export interface OrderBillingDetails {
  fullName?: string;
  phone?: string;
  email?: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  tax: string;
}

function fromCreateOrderInput(input: CreateOrderInput, source: "WL" | "FM") {
  return {
    fullname: input.fullName,
    first_name: input.firstName || undefined,
    last_name: input.lastName || undefined,
    company_name: input.companyName,
    phone: input.phoneNumber,
    locale: input.locale,
    source,
    email: input.email,
    booking_reason: input.bookingReason,
    quote_id: input.quoteID,
    partner_id: input.partnerID,
    partner_offering_id: input.partnerOfferingID,
    promotion_code: input.promoCode,
    confirm_booking_wo_payment: input.confirmWithoutPayment,
    marketing_consent: input.marketingConsent,
    additional_notes: input.additionalNotes,
    special_instructions: input.specialInstructions,
    membership_number: input.membershipNumber,
    billing_details:
      input.confirmWithoutPayment || input.skipBillingDetails
        ? undefined
        : {
            full_name: input.billing.fullName || undefined,
            phone: input.billing.phone || undefined,
            email: input.billing.email || undefined,
            street_address: input.billing.streetAddress || undefined,
            city: input.billing.city || undefined,
            state: input.billing.state || undefined,
            postal_code: input.billing.postalCode || undefined,
            country: input.billing.country || undefined,
            tax_number: input.billing.tax || undefined,
          },
    analytics: {
      ga: {
        utm_id: input.analytics?.ga?.utmId,
        utm_source: input.analytics?.ga?.utmSource,
        utm_medium: input.analytics?.ga?.utmMedium,
        utm_campaign: input.analytics?.ga?.utmCampaign,
        utm_term: input.analytics?.ga?.utmTerm,
        utm_content: input.analytics?.ga?.utmContent,
      },
    },
    stripe_card_token: input.stripeCardToken,
    contract_signature: fromContractSignatureInput(input.contractSignature),
    order_items: input.orderItems.map(fromOrderItemInput),
  };
}

export function toOrder(data: any): Order {
  return {
    id: data.id,
    confirmationID: data.confirmation_id,
    status: data.status,
    paymentFailedReason: data.payment_failed_reason ?? undefined,
    paymentFailedAt: data.payment_failed_at ?? undefined,
    subTotal: data.sub_total,
    subTotalBeforeDiscount: data.sub_total_before_discount,
    subscriptionFirstPaymentAmount: data.subscription_first_payment_amount,
    additionalNotes: data.additional_notes,
    currencyCode: data.currency,
    stripeClientSecret: data.stripe_client_secret,
    totalPrice: data.total_price,
    totalPriceInUsd: data.total_price_in_usd,
    fullName: data.fullname,
    email: data.email,
    companyName: data.company_name,
    bookingReason: data.booking_reason,
    inviteID: data.invite_id,
    phoneNumber: data.phone,
    paidThrouTeamsSubscription: data.paid_throu_teams_subscription,
    billingDetails: data.billing_details
      ? {
          fullName: data.billing_details.full_name,
          phone: data.billing_details.phone,
          email: data.billing_details.email,
          streetAddress: data.billing_details.street_address,
          city: data.billing_details.city,
          state: data.billing_details.state,
          postalCode: data.billing_details.postal_code,
          country: data.billing_details.country,
          tax: data.billing_details.tax_number,
        }
      : null,
    orderItems: data.order_items.map(toOrderItem),
    feeLines: data.fee_lines?.map(toFeeLine),
    recurringBooking: toRecurringBooking(data.recurring_booking),
    paymentMethodDetails: toPaymentMethod(data.payment_method_details),
    createdAt: new Date(data.created_at * 1000),
    taxLine: data.tax_line
      ? {
          amount: data.tax_line.amount,
          description: data.tax_line.description,
          percentage: data.tax_line.percentage,
        }
      : null,
    promotionLine: data.promotion_line
      ? {
          amount: data.promotion_line.amount,
          description: data.promotion_line.description,
          code: data.promotion_line.code,
          discount: data.promotion_line.discount,
          discountType: data.promotion_line.discount_type,
          itemPriceAfterDiscount: data.promotion_line.item_price_after_discount,
        }
      : null,
    source: data.source,
    sourceLabel: data.source_label,
    cancellable: data.cancellable,
    uncancellableReason: data.uncancellable_reason,
    cancellationLockedUntil: data.cancellation_locked_until ?? null,
    refundableAmount: data.refundable_amount ?? undefined,
    refundPercentage: data.refund_percentage ?? undefined,
    refundedAmount: data.refunded_amount ?? undefined,
    // Absent means "this payload does not answer the question", which is not a
    // yes: every other order payload would otherwise offer the change action.
    amendable: data.amendable ?? false,
    notAmendableReason: data.unamendable_reason ?? undefined,
    changesAllowedUntil: data.amendments_allowed_until ?? undefined,
    rootOrderId: data.root_order_id ?? undefined,
    calendarLinks: data.calendar_links
      ? {
          icsUrl: data.calendar_links.ics_url ?? undefined,
          googleUrl: data.calendar_links.google_url ?? undefined,
        }
      : undefined,
    cancelledAt: data.cancelled_at,
    quoteId: data.quote_id,
    currency: data.currency,
    amendedOrders: data.amended_orders?.map(toOrder),
    specialInstructions: data.special_instructions,
  };
}

function toPaymentMethod(data: any): PaymentMethod | null {
  if (!data || !data.card) {
    return null;
  }

  return {
    card: {
      last4: data.card.last4,
      network: data.card.network,
      networkLogoUrl: data.card.network_logo_url,
    },
  };
}

export function useCreateOrder() {
  const post = usePost();

  return async (input: CreateOrderInput): Promise<Order> => {
    const data = fromCreateOrderInput(input, "WL");

    const response = await post("/api/v2/orders", data);

    return toOrder(response);
  };
}

export interface InviteAttendeeInput {
  name: string;
  email: string;
  phone: string;
}

export interface SendOrderInvitesInput {
  message: string;
  attendees: InviteAttendeeInput[];
}

export interface OrderInvites {
  id: string;
  message: string;
  attendees: InviteAttendeeInput[];
}

export function useSendOrderInvites() {
  const post = usePost();

  return async (
    orderID: string,
    input: SendOrderInvitesInput
  ): Promise<OrderInvites> => {
    const response = await post(`/api/v2/orders/${orderID}/invites`, input);

    return response;
  };
}

interface UpdateOrderInput {
  id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  companyName: string;
  email: string;
  locale: string;
  phoneNumber: string;
  bookingReason?: string;
  promoCode?: string;
  confirmWithoutPayment: boolean;
  orderItems: OrderItemInput[];
  additionalNotes?: string;
  marketingConsent?: boolean;
  membershipNumber?: string;
  stripeCardToken?: string;
  contractSignature?: ContractSignatureInput;
}

export type OrderSource = "WL" | "FM";

function fromUpdateOrderInput(input: UpdateOrderInput, source: OrderSource) {
  return {
    id: input.id,
    fullname: input.fullName,
    first_name: input.firstName || undefined,
    last_name: input.lastName || undefined,
    locale: input.locale,
    company_name: input.companyName,
    phone: input.phoneNumber,
    promotion_code: input.promoCode,
    source,
    email: input.email,
    membership_number: input.membershipNumber,
    confirm_booking_wo_payment: false,
    additional_notes: input.additionalNotes,
    marketing_consent: input.marketingConsent,
    stripe_card_token: input.stripeCardToken,
    contract_signature: fromContractSignatureInput(input.contractSignature),
    order_items: input.orderItems.map(fromOrderItemInput),
  };
}

export function useUpdateOrder() {
  const put = usePut();

  return async (input: UpdateOrderInput): Promise<Order> => {
    const data = fromUpdateOrderInput(input, "WL");

    const response = await put(`/api/v2/orders/${data.id}`, data);

    return toOrder(response);
  };
}

export function useOrderQueryCallback() {
  const get = useGet();
  const { locale } = useI18n();

  return useCallback(
    async (orderID: string): Promise<Order> => {
      const response = await get(
        `/api/v2/orders/${orderID}?locale=${locale.languageTag}`
      );

      return toOrder(response);
    },
    [get, locale.languageTag]
  );
}

export function useQuoteV2Query(quoteId: string) {
  const { locale } = useI18n();
  const { data } = useSWR(
    `/api/v2/quotes/${quoteId}?locale=${locale.languageTag}`,
    {
      suspense: true,
    }
  );

  return toQuote(data);
}

export function useQuoteMaybeQuery(quoteId?: string) {
  const { locale } = useI18n();
  const { data } = useSWR(
    quoteId ? `/api/v2/quotes/${quoteId}?locale=${locale.languageTag}` : null
  );

  return data ? toQuote(data) : undefined;
}

export function useValidatePromoQuery() {
  const get = useGet();
  const { locale } = useI18n();

  return async (
    code: string,
    partnerID: string,
    promotableType: string,
    promotableID: string,
    email?: string,
    bookingStartDate?: string,
    bookingEndDate?: string,
    bookingDate?: string,
    bookingStartTime?: string,
    bookingEndTime?: string,
    quantity?: number,
    source?: string,
    partnerOfferingID?: string
  ) => {
    const apiPath = `/api/v2/partners/${partnerID}/promotions/${code}/valid`;

    const params = new URLSearchParams();
    params.append("locale", locale.languageTag);
    params.append("promotable_type", promotableType);
    params.append("promotable_id", promotableID);
    params.append("source", source || SourceType.WL);
    if (email) {
      params.append("email", email);
    }
    if (bookingStartDate && bookingEndDate) {
      params.append("start_date", bookingStartDate);
      params.append("end_date", bookingEndDate);
    } else if (bookingDate) {
      params.append("date", bookingDate);
      if (bookingStartTime && bookingEndTime) {
        params.append("start_time", bookingStartTime);
        params.append("end_time", bookingEndTime);
      }
    }

    if (quantity) {
      params.append("quantity", String(quantity));
    }

    if (partnerOfferingID) {
      params.append("partner_offering_id", partnerOfferingID);
    }

    const response: { valid: boolean; errors: string[] } = await get(
      `${apiPath}?${params.toString()}`
    );
    return response;
  };
}

export function useOrderQuery(orderID: string) {
  const { locale } = useI18n();
  const { data } = useSWR(
    `/api/v2/orders/${orderID}?locale=${locale.languageTag}`,
    {
      suspense: true,
    }
  );

  return toOrder(data);
}

// The customer's manage page reads the order by the signed token in its link,
// never by the order id (Order::MANAGE_ORDER_SIGNED_ID_PURPOSE). Same payload as
// `useOrderQuery`; not suspended, because a mangled token is an ordinary outcome
// here (mail scanners rewrite links) and the page words it rather than throwing.
export function manageOrderKey(token: string): string {
  return `/api/v2/manage_order/${token}`;
}

// A signed id is `<payload>--<digest>`, url-safe base64 on both sides. Mail
// scanners rewrite links to a short opaque id that fails this shape, and the
// confirmation page stops before fetching for the same reason (isUUID): the
// request would only 404 and page Sentry.
export function isManageOrderToken(token: string | undefined): token is string {
  return (
    typeof token === "string" &&
    token.length >= 40 &&
    /^[A-Za-z0-9_=-]+--[A-Za-z0-9_=-]+$/.test(token)
  );
}

export function useManageOrderQuery(token: string | undefined): {
  order?: Order;
  error?: unknown;
} {
  const { data, error } = useSWR(
    isManageOrderToken(token) ? manageOrderKey(token) : null
  );
  const order = useMemo(() => (data ? toOrder(data) : undefined), [data]);

  return { order, error };
}

// Frees one date of a recurring booking without refunding it. The endpoint
// answers with the refreshed order, which replaces the cached one so the page
// re-renders from a single payload. A refusal arrives as an `APIError` carrying
// the server's own wording and a machine `code`.
export function useReleaseOrderDate() {
  const post = usePost();

  return useCallback(
    async (token: string, bookingID: string): Promise<Order> => {
      const payload = await post(
        `${manageOrderKey(token)}/bookings/${bookingID}/release`
      );
      await mutate(manageOrderKey(token), payload, false);

      return toOrder(payload);
    },
    [post]
  );
}

export function useOrderInviteQuery(orderID: string): {
  data: OrderInvites[];
  refetch: () => Promise<void>;
} {
  const { data, mutate } = useSWR(`/api/v2/orders/${orderID}/invites`, {
    suspense: true,
  });

  return {
    data,
    refetch: mutate,
  };
}

/** Non-suspense version for use inside pages that already handle loading (e.g. order details) to avoid Suspense boundary replacing content with FMPageLoader. Uses same loading idiom as usePartnerOrderQueryV2: loading = !data && !error. */
export function useOrderInviteQueryV2(orderID: string): {
  data: OrderInvites[] | undefined;
  error: unknown;
  loading: boolean;
  /** Read the list back after sending, so the seats just filled stop being offered. */
  refetch: () => Promise<void>;
} {
  const { data, error, mutate } = useSWR(`/api/v2/orders/${orderID}/invites`, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return {
    data,
    error,
    loading: !data && !error,
    refetch: async () => {
      await mutate();
    },
  };
}

export function usePartnerOrderQuery(partnerID: string, orderID: string) {
  const { data } = useSWR(`/api/v2/partners/${partnerID}/orders/${orderID}`, {
    suspense: true,
  });

  return toOrder(data);
}

function fromOrderItemAddOnInput(input: AddOnOrderParams) {
  return {
    space_offering_id: input.spaceOfferingID,
    quantity: input.quantity,
    date: input.date,
  };
}

// Undefined stays undefined so an ordinary booking's wire payload is unchanged
// — the server switches on the key's presence.
function fromRecurrenceInput(recurrence?: RecurrenceInput) {
  if (!recurrence) return undefined;
  return {
    frequency: recurrence.frequency,
    interval: recurrence.interval,
    count: recurrence.count,
    dates: recurrence.dates,
    skipped_date_reasons: recurrence.skippedDateReasons,
  };
}

export function fromOrderItemInput(input: OrderItemInput) {
  if ("type" in input) {
    if (input.type === OrderableType.Booking) {
      if (input.details.type === BookingType.Daily) {
        return {
          type: input.type,
          quantity: input.quantity,
          details: {
            attendees: input.details.legacy__details.attendees,
            space_id: input.details.spaceID,
            space_layout_id: input.details.legacy__details.layoutID,
            start_date: input.details.startDate,
            end_date: input.details.endDate,
            booking_dates: input.details.bookingDates,
            physical_space_id: input.details.physicalSpaceID,
            event_type_id: input.details.eventTypeId,
            event_type_description: input.details.eventTypeDescription,
            recurrence: fromRecurrenceInput(input.details.recurrence),
          },
          space_offerings: input.spaceOfferings?.map(fromOrderItemAddOnInput),
        };
      }

      return {
        type: input.type,
        // Hourly day passes book a team's worth of passes for one window, so the
        // quantity must reach the wire (the Daily branch above already sends it).
        // Day offices leave it undefined — a single unit per booking.
        quantity: input.quantity,
        details: {
          attendees: input.details.legacy__details.attendees,
          space_id: input.details.spaceID,
          space_layout_id: input.details.legacy__details.layoutID,
          date: input.details.date,
          start_time: input.details.startTime,
          end_time: input.details.endTime,
          start_date: input.details.startDate,
          end_date: input.details.endDate,
          event_type_id: input.details.eventTypeId,
          event_type_description: input.details.eventTypeDescription,
          recurrence: fromRecurrenceInput(input.details.recurrence),
        },
        space_offerings: input.spaceOfferings?.map(fromOrderItemAddOnInput),
      };
    } else {
      return {
        type: input.type,
        details: {
          space_id: input.details.spaceID,
          add_on_id: input.details.addOnID,
          space_offering_id: input.details.spaceOfferingID,
        },
      };
    }
  } else {
    return {
      details: {
        membership_id: input.details.membershipID,
        subscription_interval: input.details.subscriptionInterval,
        subscription_start_date: input.details.subscriptionStartDate,
      },
    };
  }
}

export interface MultiPassAccess {
  id: string;
  unitPrice: number;
  totalPrice: number;
  createdAt: number; // unix timestamp in sec
  numberOfEntries: number;
  expired: boolean; // false
  name: string;
  expirationDate: string;
  bookingsUsedCount: number;
  bookingsUnusedCount: number;

  // location space
  space: {
    id: string;
    name: string;
    thumbnailImage: string;
  };

  // location
  location: {
    id: string;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };

  // orders
  order: {
    id: string;
    confirmationID: string;
    fullname: string;
    email: string;
    currency: string;
    quoteId: string;
  };

  // bookings
  bookings: MultiPassAccessBooking[];
}

export interface MultiPassAccessBooking {
  id: string;
  date: string;
  past: boolean; // in the future
  orderConfirmationID: string;
  orderID: string;
}

function toMultiPassAccess(data: any): MultiPassAccess {
  return {
    id: data.id,
    unitPrice: data.unit_price,
    totalPrice: data.total_price,
    createdAt: data.created_at,
    numberOfEntries: data.number_of_entries,
    expired: data.expired,
    name: data.name,
    expirationDate: data.expiration_date,
    bookingsUsedCount: data.bookings_used_count,
    bookingsUnusedCount: data.bookings_unused_count,
    space: {
      id: data.space.id,
      name: data.space.name,
      thumbnailImage: data.space.thumbnail_image,
    },
    location: {
      id: data.location.id,
      name: data.location.name,
      streetAddress: data.location.street_address,
      city: data.location.city,
      state: data.location.state,
      postalCode: data.location.postal_code,
      country: data.location.country,
    },
    order: {
      id: data.order.id,
      confirmationID: data.order.confirmation_id,
      fullname: data.order.fullname,
      email: data.order.email,
      currency: data.order.currency,
      quoteId: data.order.quote_id,
    },
    bookings: data.bookings.map(toMultiPassAccessBooking),
  };
}

function toMultiPassAccessBooking(data: any) {
  return {
    id: data.id,
    date: data.date,
    past: data.past,
    orderConfirmationID: data.order_confirmation_id,
    orderID: data.order_id,
  };
}

export function useCancelMultiPassBooking() {
  const post = usePost();

  return async (bookingID: string) => {
    const response = await post(`/api/v2/bookings/${bookingID}/cancel`);

    return response;
  };
}

interface CreateMultiPassBookingInput {
  date: string;
}

export function useCreateMultiPassBooking() {
  const post = usePost();

  return async (
    partnerID: string,
    multiPassID: string,
    input: CreateMultiPassBookingInput
  ) => {
    const response = await post(
      `/api/v2/partners/${partnerID}/accesses/${multiPassID}/bookings`,
      input
    );

    return toOrderItemDayPassDetails(response);
  };
}

function useSpaceOfferings({
  spaceID,
  whitelistedAddOnIds,
  parentBookingDates,
  parentBookingStartAt,
  bookingUnit,
  includeDashboardOnly,
}: {
  spaceID?: string;
  whitelistedAddOnIds?: string[];
  parentBookingDates?: string[];
  parentBookingStartAt?: string;
  bookingUnit?: BookingUnit;
  includeDashboardOnly?: boolean;
}): {
  error: any;
  loading: boolean;
  data?: SpaceOffering[];
} {
  const searchParams = new URLSearchParams();
  const { locale } = useI18n();

  searchParams.append("locale", locale.languageTag);
  if (includeDashboardOnly) {
    searchParams.append("include_dashboard_only", "true");
  }
  // Lets the serializer return per-date day-pass add-on availability; included
  // in the SWR key so changing the parent dates re-fetches availability.
  (parentBookingDates || []).forEach((date) =>
    searchParams.append("parent_booking_dates[]", date)
  );
  // An add-on's lead-time cutoff is a moment, not a day, so the booking's start
  // time decides whether it has passed. In the key as well, so moving the time
  // within one date re-fetches rather than reusing the previous answer.
  if (parentBookingStartAt) {
    searchParams.append("parent_booking_start_at", parentBookingStartAt);
  }
  // In the SWR key as well as the query, so switching between hourly and daily
  // re-fetches rather than reusing the previous unit's answer.
  if (bookingUnit) {
    searchParams.append("booking_unit", bookingUnit);
  }

  const { data, error } = useSWR(
    spaceID
      ? `/api/v2/spaces/${spaceID}/space_offerings?${searchParams.toString()}`
      : null
  );
  return {
    error: error || "",
    loading: !!(spaceID && !data),
    data: data
      ? data
          .map(toSpaceOffering)
          // Belt-and-braces for the customer path: the server already excludes
          // staff-only add-ons unless asked for them, so this only catches a
          // stale response. Staff surfaces that opted in keep the full list.
          .filter(
            (so: SpaceOffering) => includeDashboardOnly || !so.dashboardOnly
          )
          .filter(
            (so: SpaceOffering) =>
              !whitelistedAddOnIds ||
              !!whitelistedAddOnIds?.includes(so.addOnID)
          )
          .sort((offeringA: SpaceOffering, offeringB: SpaceOffering) => {
            return (
              (offeringA.numberOfEntries || 0) -
              (offeringB.numberOfEntries || 0)
            );
          })
      : undefined,
  };
}

// The parent line the add-ons hang off — the dates it covers and, when it is
// hourly, the moment it starts. Grouped because the serializer reads them as
// one booking: the start decides its own date, the rest fall back to the day.
interface ParentBooking {
  dates?: string[];
  startAt?: string;
  // Hourly or daily. Carried alongside the dates so the server can drop add-ons
  // an operator offers on full-day bookings only.
  bookingUnit?: BookingUnit;
}

export function useSpaceOfferingsQuery(
  spaceID?: string,
  whitelistedAddOnIds?: string[],
  parentBooking?: ParentBooking
) {
  return useSpaceOfferings({
    spaceID,
    whitelistedAddOnIds,
    parentBookingDates: parentBooking?.dates,
    parentBookingStartAt: parentBooking?.startAt,
    bookingUnit: parentBooking?.bookingUnit,
  });
}

// Staff-only add-ons are hidden from customers but remain appliable by staff,
// so the dashboard's order surfaces opt into the full list. Kept separate from
// the customer query so the safe default can never be lost at a call site.
export function useDashboardSpaceOfferingsQuery(spaceID?: string) {
  return useSpaceOfferings({ spaceID, includeDashboardOnly: true });
}

// availability_by_date was a raw pass-through until capacity was added to it.
// It needs a real mapper now: remaining_units would otherwise arrive snake_case
// and every reader would see undefined — i.e. an uncapped day — on exactly the
// offerings that are capped.
function toSpaceOfferingDateAvailability(
  data: any
): SpaceOfferingDateAvailability {
  return {
    date: data.date,
    available: data.available,
    remainingUnits: data.remaining_units ?? null,
  };
}

function toSpaceOffering(data: any): SpaceOffering {
  return {
    id: data.id,
    name: data.name,
    displayName: data.display_name,
    description: data.description,
    shortDescription: data.short_description,
    price: data.price,
    productCategory: data.product_category,
    productTypeName: data.product_type_name,
    numberOfEntries: data.number_of_entries,
    daysToExpire: data.days_to_expire,
    spaceID: data.space_id,
    addOnID: data.add_on_id,
    pricingType: data.pricing_type,
    minQuantity: data.min_quantity,
    priceUnit: data.price_unit,
    iconURL: data.icon_url,
    dashboardOnly: data.dashboard_only,
    availabilityByDate: data.availability_by_date?.map(
      toSpaceOfferingDateAvailability
    ),
    images: (data.images ?? []).map(toImage),
  };
}

export function useSpaceOfferingQuery(
  spaceID?: string,
  addOnID?: string
): SpaceOffering | null {
  const { data: spaceOfferings } = useSpaceOfferingsQuery(
    spaceID && addOnID ? spaceID : undefined
  );

  if (!spaceOfferings) {
    return null;
  }

  return (
    spaceOfferings.find((i: SpaceOffering) => i.addOnID === addOnID) || null
  );
}

export function useCancelOrder() {
  const post = usePost();

  return async (orderID: string, cancelReason: string) => {
    const response = await post(`/api/v2/orders/${orderID}/cancel`, {
      cancel_reason: cancelReason,
    });

    return response;
  };
}

export function useAccessAddOnMaybeQuery(
  partnerID: string,
  multiPassID?: string
) {
  const { data } = useSWR(
    multiPassID
      ? `/api/v2/partners/${partnerID}/accesses/${multiPassID}`
      : null,
    {
      suspense: true,
    }
  );

  return data ? toMultiPassAccess(data) : data;
}

export function useMultiAccessesQuery(
  partnerID: string,
  config: ConfigInterface = { suspense: true }
): {
  data: any;
  mutate: (data?: any, shouldRevalidate?: boolean | undefined) => Promise<any>;
} {
  const { data, mutate } = useSWR(
    `/api/v2/partners/${partnerID}/accesses`,
    config
  );

  return { data, mutate };
}

export async function invalidatePartnerAccessCache(partnerId: string) {
  const keys = cache.keys();
  const spaceKeys = keys.filter((k) => k.includes(partnerId));

  for (let k of spaceKeys) {
    await mutate(k);
  }
}

export function useMultiPassAvailabilityQuery(
  partnerID: string,
  multiPassID?: string
) {
  const { data, error } = useSWR(
    multiPassID
      ? `/api/v2/partners/${partnerID}/accesses/${multiPassID}/available_days`
      : null
  );

  return {
    error: error || "",
    loading: !!(multiPassID && !data),
    data: data ? toMultiPassAvailability(data) : undefined,
  };
}

export function useCreatePartnerOrder() {
  const post = usePartnerPost();

  return async (input: CreateOrderInput): Promise<Order> => {
    const data = fromCreateOrderInput(input, "FM");

    const response = await post("/api/v2/partners/orders", data);

    return toOrder(response);
  };
}

export function useUpdatePartnerOrder() {
  const put = usePartnerPut();

  return async (input: UpdateOrderInput): Promise<Order> => {
    const data = fromUpdateOrderInput(input, "FM");

    const response = await put(`/api/v2/partners/orders/${data.id}`, data);

    return toOrder(response);
  };
}

export function useLocationSpacesQuery(locationID: string) {
  const response = useSWR([locationID], fetchLocationSpaces, {
    suspense: true,
  });
  const [cache, setCache] = useState(response.data || []);

  useEffect(() => {
    if (response.data) {
      setCache(response.data);
    }
  }, [response.data]);
  return {
    data: cache,
    loading: !response.data,
  };
}

interface BookingsParam {
  startDate: string;
  endDate: string;
}

export function useGetPartnerLocationBookings(
  partnerID: string,
  locationID: string,
  param: BookingsParam
) {
  const response = useSWR(
    `/api/v2/bookings?partner_id=${partnerID}&location_id=${locationID}&start_date=${param.startDate}&end_date=${param.endDate}`
  );

  return {
    data: (response.data ? response.data.map(toBooking) : []) as Booking[],
    loading: !response.data && !response.error,
  };
}

function toBooking(data: any): Booking {
  const duration =
    data.end_time && data.start_time
      ? String(getDurationInHours(data.end_time, data.start_time))
      : data.duration;

  return {
    id: data.id,
    attendees: data.attendees,
    spaceID: data.space_id,
    spaceType: data.space_type,
    bookingType: data.booking_type,
    layoutID: data.space_layout_id,
    date: data.date,
    time: data.time || data.start_time,
    duration: duration,
    startDate: data.start_date,
    endDate: data.end_date,
    room: data.name,
    days: data.days || data.duration,
    floor: data.floor,
    orderID: data.order_id,
    originalOrderId: data.original_order_id,
    orderConfirmationID: data.order_confirmation_id,
    orderCreatedAt: new Date(data.order_created_at * 1000), // unix timestamp in SECONDS
    orderName: data.order_fullname,
    orderEmail: data.order_email,
    physicalSpaceID: data.physical_space_id,
    cancellationAllowed: data.cancellation_allowed,
    cancellationAllowedBefore: data.cancellation_allowed_before
      ? // unix timestamp in SECONDS
        new Date(data.cancellation_allowed_before * 1000)
      : null,
    productType: data.product_type,
  };
}

async function fetchLocationSpaces(locationID: string) {
  const res = await fetch(`/api/v2/locations/${locationID}/spaces`);

  const body = await res.json();

  if (res.status >= 200 && res.status < 300) {
    const spaces: Space[] = body.map(toSpace);
    return spaces;
  }

  throw new APIError(body.message);
}

export async function invalidateOrderCache(orderID: string) {
  const keys = cache.keys();
  // Delete any cache that includes the orderID in its key
  const orderIDKeys = keys.filter((k) => k.includes(orderID));

  for (let k of orderIDKeys) {
    await mutate(k);
  }
}
export async function invalidateOrderLineItemCache(orderItemID?: string) {
  if (!orderItemID) return;
  const keys = cache.keys();
  // Delete any cache that includes the orderItemID in its key
  let orderItemIDKeys = keys.filter((k) => k.includes(orderItemID));

  orderItemIDKeys = orderItemIDKeys.concat(
    keys.filter((k) => k.includes("order_line_items"))
  );

  for (let k of orderItemIDKeys) {
    await mutate(k);
  }
}

export function usePost() {
  const { token } = useAuth();
  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Flexspace-Client": "partners-portal",
          // Sent only when there is one to send, as PUT already does. Most
          // callers here are the anonymous storefront, where the portal token
          // is empty and `Bearer ` is not a credential — it is a header no
          // endpoint on this path reads today, so this is symmetry with the
          // other verbs rather than a fix for anything observable.
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(
        body.message || body.errors?.[0],
        body.code,
        response.status,
        body.dates
      );
    },
    [token]
  );
}

function useGet() {
  const { token } = useAuth();

  return useCallback(
    // The caller names the shape it expects, because a parsed body is only
    // ever `unknown` here — the conversion to a domain type happens at each
    // call site, against the endpoint that call site actually reads.
    async <T>(url: string): Promise<T> => {
      // These reads sit outside SWR, so they miss the transport replay its
      // clients get. WebKit surfaces a dropped connection as a bare TypeError
      // where Chromium quietly retries it, and without this the difference
      // reaches the UI as a failed page load.
      const { status, body } = await requestJson(url, {
        headers: {
          "Content-Type": "application/json",
          "Flexspace-Client": "partners-portal",
          Authorization: `Bearer ${token}`,
        },
      });

      if (status >= 200 && status < 300) {
        return body as T;
      }

      throw new APIError(toErrorBody(body).message);
    },
    [token]
  );
}

function usePut() {
  const { token } = useAuth();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Flexspace-Client": "partners-portal",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      // Carries `dates` the way POST does: a re-submit of a recurring booking
      // is refused through this path, and dropping them costs the customer the
      // list of dates they lost in favour of the generic sentence.
      throw new APIError(body.message, body.code, response.status, body.dates);
    },
    [token]
  );
}

export function useDelete() {
  const { token } = useAuth();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Flexspace-Client": "partners-portal",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message);
    },
    [token]
  );
}

function usePartnerPost() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message);
    },
    [token]
  );
}

function usePartnerPut() {
  const { token } = useAuth0();

  return useCallback(
    async (url: string, data?: any) => {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(data),
      });

      const body = await response.json();

      if (response.status >= 200 && response.status < 300) {
        return body;
      }

      throw new APIError(body.message);
    },
    [token]
  );
}

interface MultiPassAvailability {
  id: string;
  spaceID: string;
  email: string;
  availableDays: AvailableDay[];
}

export interface AvailableDay {
  date: string;
  availableFullDay: boolean;
  // Whether the requested number of passes can be booked by the hour on this day
  // (>= quantity units share a bookable window). Distinct from availableFullDay so
  // the Hourly and Daily tabs can disable a date independently. Optional: callers
  // that don't compute it (and the legacy API) leave it undefined, which the picker
  // treats as bookable — only an explicit false hides the date on the hourly tab.
  availableHourly?: boolean;
  openAt: string;
  closedAt: string;
}

function toMultiPassAvailability(data: any): MultiPassAvailability {
  return {
    id: data.access_id,
    spaceID: data.space_id,
    email: data.email,
    availableDays: data.data.map(toAvailableDay),
  };
}

function toAvailableDay(data: any): AvailableDay {
  return {
    date: data.date,
    availableFullDay: data.available_full_day,
    // Default to bookable when the field is absent (older API), so a single-pass
    // hourly tab keeps showing every available day until the backend sends it.
    availableHourly: data.available_hourly ?? true,
    openAt: data.open_at,
    closedAt: data.closed_at,
  };
}

interface DateRangeParams {
  startDate: string;
  endDate: string;
}

function toSpaceDateRange(
  param: DateRangeParams & {
    capacity?: number;
    physicalSpaceID?: string;
    subscriptionId?: string;
    quantity?: number;
  }
) {
  const searchParams = new URLSearchParams();

  searchParams.set("start_date", param.startDate);
  searchParams.set("end_date", param.endDate);

  if (param.capacity) {
    searchParams.set("capacity", `${param.capacity}`);
  }
  if (param.physicalSpaceID) {
    searchParams.set("physical_space_id", `${param.physicalSpaceID}`);
  }
  if (param.subscriptionId) {
    searchParams.set("subscription_id", `${param.subscriptionId}`);
  }
  // Only send quantity when a team wants > 1 pass; omitting it keeps the URL (and
  // SWR cache key) identical to the single-pass case, so existing callers are unaffected.
  if (param.quantity && param.quantity > 1) {
    searchParams.set("quantity", `${param.quantity}`);
  }

  return `?${searchParams.toString()}`;
}

export function toAvailableDays(data: any): AvailableDay[] {
  return data.data.map(toAvailableDay);
}

interface AvailableDayConfig {
  enabled: PricingType[];
  multiDayBookingAllowed: boolean;
}

export function toAvailableDayConfig(data: any): AvailableDayConfig {
  return {
    enabled: data.config?.enabled || [],
    multiDayBookingAllowed: !!data.config?.multi_day_booking,
  };
}

export function useGetSpaceAvailableDays(
  spaceID?: string,
  param?: DateRangeParams & {
    capacity?: number;
    physicalSpaceID?: string;
    subscriptionId?: string;
    quantity?: number;
  }
): {
  // Whatever the fetcher threw, not a message. Declaring it a string invites a
  // caller to interpolate it, which yields the error's class name — minified,
  // and so different in every build — in whatever it was interpolated into.
  error: unknown;
  loading: boolean;
  data: { config: AvailableDayConfig; availableDays?: AvailableDay[] };
} {
  const response = useSWR(
    param && spaceID
      ? `/api/v2/spaces/${spaceID}/available_days${toSpaceDateRange(param)}`
      : null,
    { dedupingInterval: 10000 }
  );

  const availableDays = useMemo(
    () => (response.data ? toAvailableDays(response.data) : undefined),
    [response.data]
  );

  return {
    error: response.error,
    loading: !!(param && spaceID && !response.data),
    data: response.data
      ? {
          config: toAvailableDayConfig(response.data),
          availableDays,
        }
      : {
          config: { enabled: [], multiDayBookingAllowed: false },
          availableDays,
        },
  };
}

function toLocationBlockOutDaysParams(locationIDs: string[]) {
  const searchParams = new URLSearchParams();

  // Sorted ids and a fixed 12-month window keep the SWR key stable across
  // selection order and month paging, so the calendar never refetches.
  const today = new Date();
  searchParams.set("ids", [...locationIDs].sort().join(","));
  searchParams.set("start_date", toDay(today));
  searchParams.set("end_date", toDay(endOfMonth(addMonths(today, 12))));

  return `?${searchParams.toString()}`;
}

export function useLocationBlockedDays(locationIDs: string[]): {
  blockedDays: Set<string> | undefined;
  loading: boolean;
} {
  const response = useSWR(
    locationIDs.length
      ? `/api/v2/locations/block_out_days${toLocationBlockOutDaysParams(
          locationIDs
        )}`
      : null,
    { dedupingInterval: 10000 }
  );

  // A date is blocked only when every requested location blocks it: if one
  // selected location is still open, its rooms are bookable on that date and
  // the results filter narrows the list. Locations absent from the payload
  // have no blockouts, so any miss clears the date.
  const blockedDays = useMemo(() => {
    // Blockout data is advisory: the results filter and checkout still enforce
    // real availability. A failed request must not read as "still loading"
    // (which pickers render as nothing selectable), so fail open and let every
    // date stay pickable. The global fetcher has already reported the error.
    if (response.error) {
      return new Set<string>();
    }
    if (!response.data) {
      return undefined;
    }
    const byLocation = new Map<string, Set<string>>(
      response.data.data.map(
        (entry: { location_id: string; blocked_dates: string[] }) => [
          entry.location_id,
          new Set(entry.blocked_dates),
        ]
      )
    );
    const [first, ...rest] = locationIDs.map(
      (id) => byLocation.get(id) || new Set<string>()
    );
    const intersection = [...(first || [])].filter((day) =>
      rest.every((days) => days.has(day))
    );
    return new Set(intersection);
  }, [response.data, response.error, locationIDs]);

  return {
    blockedDays,
    loading: !!(locationIDs.length && !response.data && !response.error),
  };
}

function toAvailabilityTimeSlotsParams(date: string) {
  const searchParams = new URLSearchParams();

  searchParams.set("date", date);
  searchParams.set(
    "browser_time_zone",
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  return `?${searchParams.toString()}`;
}

export interface AvailableTimeSlot {
  startTime: string;
  endTime: string[];
  price?: number;
}

function toAvailableTimeSlot(data: any): AvailableTimeSlot {
  return {
    startTime: data.start_time,
    endTime: data.end_time,
    price: data.price,
  };
}

export function toAvailableTimeSlots(data: any): AvailableTimeSlot[] {
  return data.data.map(toAvailableTimeSlot);
}

export function useGetSpaceAvailableTimeSlots(
  spaceID: string | undefined,
  date: string | undefined,
  quantity?: number,
  spaceLayoutId?: string
): { error: unknown; loading: boolean; data: AvailableTimeSlot[] } {
  // Only send quantity when a team wants > 1 pass; omitting it keeps the URL (and
  // SWR cache key) identical to the single-pass case, so existing callers are unaffected.
  const quantityParam = quantity && quantity > 1 ? `&quantity=${quantity}` : "";
  // Per-slot prices are layout-grained on a per-layout-priced space; send the
  // selected layout so the picker's "better rate" dots compare like-for-like.
  // Omitted -> URL/cache key unchanged, so other space types are unaffected.
  const layoutParam = spaceLayoutId ? `&space_layout_id=${spaceLayoutId}` : "";
  const endpoint =
    date && spaceID
      ? `/api/v2/spaces/${spaceID}/available_time_slots${toAvailabilityTimeSlotsParams(
          date
        )}${quantityParam}${layoutParam}${
          hasSmartPricingSearchParam() ? "&smart_pricing=true" : ""
        }`
      : null;
  const response = useSWR(endpoint);

  return {
    error: response.error,
    // Loading means "a request is in flight", so it must be false while idle
    // (no date picked yet, so no request fires) and false once one settles —
    // including the settle that answers with zero slots. A truthiness-only
    // check on the payload conflates all three, which strands consumers that
    // render a spinner until slots arrive on a spinner that never resolves.
    loading: !!endpoint && !response.data && !response.error,
    data: response.data ? toAvailableTimeSlots(response.data) : [],
  };
}

export function useSpaceAmenitiesQuery(spaceID?: string | null) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    spaceID ? `/api/v2/spaces/${spaceID}/amenities${qs}` : null
  );

  return {
    data: data ? data.map(toAmenity) : undefined,
    loading: spaceID && !data && !error,
    error,
  };
}

export function useMembershipAmenitiesQuery(offeringID?: string | null) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    offeringID
      ? `/api/v2/partner_offering_items/${offeringID}/amenities${qs}`
      : null
  );

  return {
    data: data ? data.map(toAmenity) : undefined,
    loading: offeringID && !data && !error,
    error,
  };
}

export function useSpaceBookingPolicyQuery(
  spaceID?: string | null,
  startDate?: string,
  endDate?: string
) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;
  if (startDate) qs += `&start_date=${startDate}`;
  if (endDate) qs += `&end_date=${endDate}`;

  const { data, error } = useSWR(
    spaceID ? `/api/v2/spaces/${spaceID}/booking_policy${qs}` : null
  );

  return {
    data: data ? toSpaceBookingPolicy(data) : undefined,
    loading: spaceID && !data && !error,
    error,
  };
}

export function usePartnerBookingPolicyQuery(partnerID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    partnerID ? `/api/v2/partners/${partnerID}/booking_policy${qs}` : null
  );

  return {
    data: data ? toPartnerBookingPolicy(data.booking_policy) : undefined,
    loading: partnerID && !data && !error,
    error,
  };
}
export function usePartnerBookingHourQuery(partnerID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    partnerID ? `/api/v2/partners/${partnerID}/booking_hour${qs}` : null
  );

  return {
    data: data ? toPartnerBookingHour(data.booking_hour) : undefined,
    loading: partnerID && !data && !error,
    error,
  };
}

export function useTestimonialsQuery(partnerOfferingID: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    partnerOfferingID
      ? `/api/v2/partner_offerings/${partnerOfferingID}/testimonials${qs}`
      : null
  );

  return {
    data: data ? data.testimonials.map(toPartnerTestimonial) : [],
    loading: partnerOfferingID && !data && !error,
    error,
  };
}

export function usePartnerOfferingFilterConfigsQuery(
  partnerOfferingID?: string
) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    partnerOfferingID
      ? `/api/v2/partner_offerings/${partnerOfferingID}/filter_configs${qs}`
      : null
  );

  return {
    data: data ? data.filter_configs.map(toPartnerOfferingFilterConfig) : [],
    loading: partnerOfferingID && !data && !error,
    error,
  };
}

// Shared so the "no images" case is one array identity rather than a fresh one
// per render — see the memo note in usePartnerOfferingImagesQuery.
const NO_PARTNER_OFFERING_IMAGES: ImageVersion[] = [];

export function usePartnerOfferingImagesQuery(partnerOfferingID?: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    partnerOfferingID
      ? `/api/v2/partner_offerings/${partnerOfferingID}/images${qs}`
      : null
  );

  // Mapping inline would hand back a new array on every render, which defeats
  // every downstream memo and effect that keys off this list — the image hooks
  // in data/image.ts all do.
  const images = useMemo(
    () =>
      data
        ? (data.images.map(toPartnerOfferingImage) as ImageVersion[])
        : NO_PARTNER_OFFERING_IMAGES,
    [data]
  );

  return {
    data: images,
    loading: partnerOfferingID && !data && !error,
    error,
  };
}

interface PartnerContactForm {
  id?: string;
  partnerID?: string;
  title?: string;
  description?: string;
  partnerPhoneBlurb?: string;
  alreadyHaveReservationEnabled?: string;
}

function toPartnerContactForm(data: any): PartnerContactForm {
  return {
    id: data.id,
    partnerID: data.partner_id,
    title: data.title,
    description: data.description,
    partnerPhoneBlurb: data.partner_phone_blurb,
    alreadyHaveReservationEnabled: data.already_have_reservation_enabled,
  };
}

export function usePartnerContactFormQuery(partnerID: string) {
  let qs = "";
  const { locale } = useI18n();

  qs += `?locale=${locale.languageTag}`;

  const { data, error } = useSWR(
    `/api/v2/partners/${partnerID}/contact_form${qs}`,
    { suspense: true }
  );

  return {
    data: data ? toPartnerContactForm(data) : undefined,
    loading: !data && !error,
    error,
  };
}

interface PaymentConfig {
  stripePublishableKey: string;
  // Present only while the location takes its own Tranzila payments.
  tranzilaTerminal?: string;
}

function toPaymentConfig(data: any): PaymentConfig {
  return {
    stripePublishableKey: data.stripe_publishable_key,
    tranzilaTerminal: data.tranzila_terminal ?? undefined,
  };
}

export function usePartnerPaymentConfigsQuery(
  partnerID: string,
  locationID?: string
): {
  data: PaymentConfig | undefined;
  loading: boolean;
  error: Error | undefined;
} {
  const params = new URLSearchParams();
  if (locationID) {
    params.append("location_id", locationID);
  }

  const queryString = params.toString();
  const url = `/api/v3/partners/${partnerID}/payment_configs${
    queryString ? `?${queryString}` : ""
  }`;

  const { data, error } = useSWR(url);

  return {
    data: data ? toPaymentConfig(data.data) : undefined,
    loading: !data && !error,
    error,
  };
}

export const handleValidateEmail = async (
  email: string,
  onError?: (email?: string, msg?: string) => void
) => {
  onError?.(undefined);
  if (email && isEmail(email)) {
    const res = await fetch(
      `/api/v3/emails/validate?email=${encodeURIComponent(email)}`,
      {
        method: "POST",
      }
    );
    const json = await res.json();
    if (json.success) {
      return true;
    }

    onError?.(email, json.message);

    return false;
  }

  return true;
};
